import { useEffect, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, PowerOff, PlayCircle, RefreshCw, Link2, ClipboardCheck, AlertOctagon, CheckCircle2, XCircle, Rocket } from "lucide-react";
import { toast } from "sonner";

const TID = {
  tab: (t) => `gov-tab-${t}`,
  connRow: (id) => `gov-conn-row-${id}`,
  killBtn: (id) => `gov-kill-${id}`,
  activateBtn: (id) => `gov-activate-${id}`,
  refreshBtn: "gov-refresh-button",
  approveBtn: (id) => `gov-queue-approve-${id}`,
  rejectBtn: (id) => `gov-queue-reject-${id}`,
  executeBtn: (id) => `gov-queue-execute-${id}`,
  queueFilter: "gov-queue-filter-select",
};

const STATUS_COLOURS = {
  active:    "bg-emerald-100 text-emerald-800",
  suspended: "bg-rose-100 text-rose-700",
  proposed:  "bg-amber-100 text-amber-800",
  testing:   "bg-sky-100 text-sky-800",
  retired:   "bg-slate-100 text-slate-500",
};

const LEVEL_COLOURS = {
  prohibited:          "bg-rose-100 text-rose-800",
  read_only:           "bg-sky-100 text-sky-800",
  draft:               "bg-amber-100 text-amber-800",
  user_approved_write: "bg-violet-100 text-violet-800",
  auto_write:          "bg-orange-100 text-orange-800",
  admin:               "bg-slate-900 text-white",
};

export default function IntegrationsAdminPage() {
  const { can, loaded } = usePermissions();
  const [tab, setTab] = useState("connections");
  const [connections, setConnections] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [audit, setAudit] = useState([]);
  const [refs, setRefs] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [systemFilter, setSystemFilter] = useState("");
  const [queueFilter, setQueueFilter] = useState("pending");

  const load = async () => {
    setLoading(true);
    try {
      const [c, e, a, r, q] = await Promise.all([
        api.get("/governance/connections"),
        api.get("/governance/endpoints"),
        api.get(`/governance/audit-log?limit=100${systemFilter ? `&system_name=${systemFilter}` : ""}`),
        api.get("/governance/references?limit=200"),
        api.get(`/governance/queue${queueFilter && queueFilter !== "__all" ? `?status=${queueFilter}` : "?status="}`),
      ]);
      setConnections(c.data); setEndpoints(e.data); setAudit(a.data); setRefs(r.data); setQueue(q.data);
    } catch (err) {
      console.warn("[Gov] load failed", err?.message || err);
      toast.error("Failed to load governance data");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (loaded && can("org_mgmt", "view")) load();
    /* eslint-disable-next-line */
  }, [loaded, systemFilter, queueFilter]);

  const decideQueueItem = async (qid, decision) => {
    const comment = decision === "rejected" ? window.prompt("Reason for rejection? (optional)") : "";
    try {
      await api.post(`/governance/queue/${qid}/decide`, { decision, comment });
      toast.success(decision === "approved" ? "Approved" : "Rejected");
      load();
    } catch (e) {
      toast.error("Failed to " + decision);
    }
  };

  const executeQueueItem = async (qid) => {
    if (!window.confirm("Execute this approved write? This will make the actual API call to the source system.")) return;
    try {
      const { data } = await api.post(`/governance/queue/${qid}/execute`);
      if (data.ok) {
        toast.success("Executed successfully");
      } else {
        toast.warning(`Executor returned failure: ${data.status}`);
      }
      load();
    } catch (e) {
      toast.error("Execution failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const killIt = async (id) => {
    if (!window.confirm("KILL this integration? All API calls from GBE Hub to this system will be blocked immediately.")) return;
    await api.post(`/governance/connections/${id}/kill`);
    toast.success("Integration killed. Kill switch is ON.");
    load();
  };
  const activate = async (id) => {
    await api.put(`/governance/connections/${id}`, { status: "active" });
    toast.success("Integration activated.");
    load();
  };

  if (!loaded) return <AppLayout title="Integrations"><div className="h-40 shimmer" /></AppLayout>;
  if (!can("org_mgmt", "view")) {
    return (
      <AppLayout title="Integrations">
        <div className="max-w-md mx-auto mt-16 text-center">
          <AlertOctagon className="w-6 h-6 text-rose-700 inline-block" />
          <h2 className="font-display text-xl font-semibold text-slate-900 mt-3">Admin access required</h2>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="API Integrations"
      subtitle="GBE Hub Governance Layer — connections, endpoint approvals, audit log, references & write-back queue"
      actions={
        <Button data-testid={TID.refreshBtn} onClick={load} disabled={loading} size="sm" variant="outline">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <KPI label="Connections" value={connections.length} icon={Link2} />
        <KPI label="Active" value={connections.filter((c) => c.status === "active").length} tone="good" icon={ShieldCheck} />
        <KPI label="Suspended" value={connections.filter((c) => c.status === "suspended").length} tone="warn" icon={PowerOff} />
        <KPI label="Approved endpoints" value={endpoints.length} icon={ClipboardCheck} />
        <KPI label="Pending writes" value={queue.filter((q) => q.status === "pending").length} tone={queue.filter((q) => q.status === "pending").length > 0 ? "warn" : "default"} icon={AlertOctagon} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-50 border border-slate-200 p-1 h-auto">
          <TabsTrigger data-testid={TID.tab("connections")} value="connections" className="text-xs data-[state=active]:bg-white">Connections</TabsTrigger>
          <TabsTrigger data-testid={TID.tab("endpoints")}   value="endpoints"   className="text-xs data-[state=active]:bg-white">Approved Endpoints</TabsTrigger>
          <TabsTrigger data-testid={TID.tab("audit")}       value="audit"       className="text-xs data-[state=active]:bg-white">Audit Log</TabsTrigger>
          <TabsTrigger data-testid={TID.tab("references")}  value="references"  className="text-xs data-[state=active]:bg-white">External References</TabsTrigger>
          <TabsTrigger data-testid={TID.tab("queue")}       value="queue"       className="text-xs data-[state=active]:bg-white">Write-Back Queue</TabsTrigger>
        </TabsList>

        <TabsContent value="connections" className="mt-4">
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="grid gap-2 px-4 py-2.5 text-[11px] uppercase tracking-wider text-white bg-gbe" style={{gridTemplateColumns:"1.4fr 1fr 0.6fr 0.9fr 1fr 0.8fr 0.6fr"}}>
              <div>System</div><div>Environment</div><div>Auth</div><div>Access</div><div>Owner</div><div>Status</div><div className="text-right">Action</div>
            </div>
            <div className="divide-y divide-slate-100">
              {connections.map((c) => (
                <div key={c.id} data-testid={TID.connRow(c.id)} className="grid gap-2 px-4 py-3 text-sm items-center" style={{gridTemplateColumns:"1.4fr 1fr 0.6fr 0.9fr 1fr 0.8fr 0.6fr"}}>
                  <div>
                    <div className="font-medium text-slate-900">{c.display_name || c.system_name}</div>
                    <div className="text-xs text-slate-500 font-mono truncate">{c.base_endpoint}</div>
                  </div>
                  <div className="text-xs text-slate-700">{c.environment}</div>
                  <div className="text-xs text-slate-700 font-mono">{c.authentication_type}</div>
                  <div className="text-xs">
                    <Badge className="bg-slate-100 text-slate-700">{c.access_mode?.replace("_"," ")}</Badge>
                  </div>
                  <div className="text-xs text-slate-600 truncate">{c.connection_owner || "—"}</div>
                  <div><Badge className={STATUS_COLOURS[c.status] || "bg-slate-100"}>{c.status}</Badge></div>
                  <div className="text-right">
                    {c.status === "active" ? (
                      <Button data-testid={TID.killBtn(c.id)} onClick={() => killIt(c.id)} size="sm" variant="outline" className="bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100 h-7 text-xs">
                        <PowerOff className="w-3 h-3 mr-1" /> Kill
                      </Button>
                    ) : (
                      <Button data-testid={TID.activateBtn(c.id)} onClick={() => activate(c.id)} size="sm" variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100 h-7 text-xs">
                        <PlayCircle className="w-3 h-3 mr-1" /> Activate
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Kill switch immediately blocks all API calls to that system. Approved endpoints and audit history are retained.
          </p>
        </TabsContent>

        <TabsContent value="endpoints" className="mt-4">
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="grid grid-cols-7 gap-2 px-4 py-2.5 text-[11px] uppercase tracking-wider text-white bg-gbe">
              <div>Entity</div><div>Method</div><div>Endpoint</div><div>Purpose</div><div>Direction</div><div>Approval Level</div><div>Enabled</div>
            </div>
            <div className="divide-y divide-slate-100">
              {endpoints.map((e) => (
                <div key={e.id} className="grid grid-cols-7 gap-2 px-4 py-2.5 text-sm items-center">
                  <div className="font-medium">{e.entity_name}</div>
                  <div><Badge className="bg-slate-900 text-white text-[10px] font-mono">{e.method}</Badge></div>
                  <div className="text-xs font-mono text-slate-700">{e.endpoint}</div>
                  <div className="text-xs text-slate-600 truncate">{e.purpose}</div>
                  <div className="text-xs">{e.direction}</div>
                  <div><Badge className={`text-xs ${LEVEL_COLOURS[e.approval_level] || "bg-slate-100"}`}>{e.approval_level?.replace(/_/g," ")}</Badge></div>
                  <div><Badge className={e.enabled ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}>{e.enabled ? "yes" : "no"}</Badge></div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <div className="flex items-center gap-2 mb-2">
            <Select value={systemFilter || "__all"} onValueChange={(v) => setSystemFilter(v === "__all" ? "" : v)}>
              <SelectTrigger className="w-56 h-8 text-xs"><SelectValue placeholder="All systems" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All systems</SelectItem>
                {connections.map((c) => <SelectItem key={c.system_name} value={c.system_name}>{c.display_name || c.system_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-500">{audit.length} audit rows</span>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="grid grid-cols-8 gap-2 px-4 py-2.5 text-[11px] uppercase tracking-wider text-white bg-gbe">
              <div>Timestamp</div><div>System</div><div>Method</div><div>Endpoint</div><div className="text-right">Status</div><div className="text-right">Records</div><div className="text-right">Duration</div><div>Result</div>
            </div>
            <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
              {audit.map((a) => (
                <div key={a.id} className="grid grid-cols-8 gap-2 px-4 py-2 text-xs items-center">
                  <div className="font-mono text-slate-500">{a.date_time?.slice(0,19).replace("T"," ")}</div>
                  <div>{a.system_name}</div>
                  <div className="font-mono">{a.method}</div>
                  <div className="font-mono truncate text-slate-700">{a.endpoint}</div>
                  <div className={`text-right font-mono ${a.response_status >= 400 ? "text-rose-700" : "text-slate-700"}`}>{a.response_status || "—"}</div>
                  <div className="text-right">{a.records_processed}</div>
                  <div className="text-right text-slate-500">{a.duration_ms} ms</div>
                  <div>
                    {a.success
                      ? <Badge className="bg-emerald-100 text-emerald-800">ok</Badge>
                      : <Badge className="bg-rose-100 text-rose-700" title={a.error_message}>fail</Badge>}
                  </div>
                </div>
              ))}
              {audit.length === 0 && <div className="p-10 text-center text-sm text-slate-500">No audit rows yet.</div>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="references" className="mt-4">
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="grid grid-cols-5 gap-2 px-4 py-2.5 text-[11px] uppercase tracking-wider text-white bg-gbe">
              <div>Entity Type</div><div>Source System</div><div>External ID</div><div>GBE Hub ID</div><div>Last Synced</div>
            </div>
            <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
              {refs.map((r) => (
                <div key={r.id} className="grid grid-cols-5 gap-2 px-4 py-2 text-xs items-center">
                  <div className="font-medium">{r.gbe_entity_type}</div>
                  <div className="font-mono text-slate-600">{r.source_system}</div>
                  <div className="font-mono text-slate-500 truncate">{r.external_record_id}</div>
                  <div className="font-mono text-slate-500 truncate">{r.gbe_entity_id?.slice(0, 12)}...</div>
                  <div className="text-slate-500">{r.last_synced_date?.slice(0, 19).replace("T"," ")}</div>
                </div>
              ))}
              {refs.length === 0 && <div className="p-10 text-center text-sm text-slate-500">No references yet.</div>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="queue" className="mt-4">
          <div className="flex items-center gap-2 mb-2">
            <Select value={queueFilter} onValueChange={setQueueFilter}>
              <SelectTrigger data-testid={TID.queueFilter} className="w-52 h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved (awaiting execute)</SelectItem>
                <SelectItem value="executed">Executed</SelectItem>
                <SelectItem value="execution_failed">Execution failed</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="__all">All</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-500">{queue.length} items</span>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="grid gap-2 px-4 py-2.5 text-[11px] uppercase tracking-wider text-white bg-gbe" style={{gridTemplateColumns:"1fr 0.7fr 0.6fr 0.9fr 1.3fr 0.8fr 1.3fr"}}>
              <div>Submitted</div><div>System</div><div>Entity</div><div>External ID</div><div>Reason</div><div>Status</div><div className="text-right">Action</div>
            </div>
            <div className="divide-y divide-slate-100">
              {queue.map((q) => (
                <div key={q.id} className="grid gap-2 px-4 py-3 text-xs items-center" style={{gridTemplateColumns:"1fr 0.7fr 0.6fr 0.9fr 1.3fr 0.8fr 1.3fr"}}>
                  <div className="text-slate-500 font-mono">{q.submitted_at?.slice(0,16).replace("T"," ")}</div>
                  <div>{q.system_name}</div>
                  <div>{q.entity_type}</div>
                  <div className="font-mono text-slate-500 truncate">{q.external_record_id}</div>
                  <div className="text-slate-700 truncate" title={q.reason}>{q.reason}</div>
                  <div>
                    <Badge className={
                      q.status === "pending" ? "bg-amber-100 text-amber-800"
                      : q.status === "approved" ? "bg-sky-100 text-sky-800"
                      : q.status === "executed" ? "bg-emerald-100 text-emerald-800"
                      : q.status === "execution_failed" ? "bg-rose-100 text-rose-700"
                      : "bg-slate-100 text-slate-500"
                    }>{q.status?.replace("_"," ")}</Badge>
                  </div>
                  <div className="text-right space-x-1">
                    {q.status === "pending" && (
                      <>
                        <Button data-testid={TID.approveBtn(q.id)} size="sm" onClick={() => decideQueueItem(q.id, "approved")} className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                        </Button>
                        <Button data-testid={TID.rejectBtn(q.id)} size="sm" variant="outline" onClick={() => decideQueueItem(q.id, "rejected")} className="h-7 text-xs bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100">
                          <XCircle className="w-3 h-3 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                    {q.status === "approved" && (
                      <Button data-testid={TID.executeBtn(q.id)} size="sm" onClick={() => executeQueueItem(q.id)} className="h-7 text-xs bg-gbe hover:bg-gbe/90 text-white">
                        <Rocket className="w-3 h-3 mr-1" /> Execute
                      </Button>
                    )}
                    {q.status === "execution_failed" && (
                      <Button size="sm" onClick={() => executeQueueItem(q.id)} className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white">
                        <Rocket className="w-3 h-3 mr-1" /> Retry
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {queue.length === 0 && <div className="p-10 text-center text-sm text-slate-500">No items in this bucket.</div>}
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Pipeline: <span className="font-mono">pending → approved → executed</span> (or <span className="font-mono">rejected</span>). Only approved items can be executed. Every action is logged in the Audit Log tab.
          </p>
        </TabsContent>
      </Tabs>
    </AppLayout>
  );
}


function KPI({ label, value, tone = "default", icon: Icon }) {
  const tones = { default:"text-slate-900", good:"text-emerald-700", warn:"text-amber-700", danger:"text-rose-700" };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
      </div>
      <div className={`mt-1 font-display text-2xl ${tones[tone]}`}>{value}</div>
    </div>
  );
}
